import { Check, CheckSquare, Play, Redo2, TriangleAlert, Trophy, Undo2, Waypoints } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

interface ExperienceTitleBarProps {
  title: string;
  saving: boolean;
  /** True after a save attempt fails for any reason other than a 409 conflict (which reloads the
   * page directly instead) — cleared at the start of the next attempt. */
  saveFailed: boolean;
  isDirty: boolean;
  canUndo: boolean;
  canRedo: boolean;
  undoLabel?: string | null;
  redoLabel?: string | null;
  onTitleSave: (title: string) => void;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  /** Show the centre tool group (Errors · Scoring · Preview) — the builder-level surfaces. Off for the
   * legacy canvas. The handlers are optional so the buttons can render as stubs before they're wired. */
  showTools?: boolean;
  onShowErrors?: () => void;
  /**
   * How many ERRORS the experience has, from `experienceProblems`. Shown as a count beside the
   * tool, because the number is the thing a teacher wants before deciding to open anything — and
   * because errors gate publication, so this is the one figure that says whether the experience
   * can ship. Warnings are deliberately not counted here: they never block, and a single number
   * mixing the two would say nothing about either.
   */
  errorCount?: number;
  /** Errors aspect is active → highlight the tool as a pressed toggle, as Scoring does. */
  errorsActive?: boolean;
  /** Select mode — a toggle, grouped with Scoring; Errors and Preview are actions. */
  selectActive?: boolean;
  onToggleSelect?: () => void;
  onShowScoring?: () => void;
  onPreview?: () => void;
  /** Scoring aspect is active → highlight the Scoring tool as a pressed toggle. */
  scoringActive?: boolean;
  /** Another tab holds write access (see useTaskTakeOver) — title becomes non-editable and
   * Undo/Redo/Save all become no-ops. */
  readOnly?: boolean;
}

/**
 * The experience editor's bar 2 — deliberately mirrors the task editor's TaskTitlebar (its own
 * component, not shared: the two carry different titles): a symbol on the left (an experience mark,
 * NOT the app logo), the editable title, and undo · redo · save · X on the right. The save button
 * carries the same Save/Saving/Saved/failed state machine and fixed min-width so undo/redo never
 * shift. No task/point counts here (dropped).
 */
export default function ExperienceTitleBar({
  title,
  saving,
  saveFailed,
  isDirty,
  canUndo,
  canRedo,
  undoLabel,
  redoLabel,
  onTitleSave,
  onSave,
  onUndo,
  onRedo,
  showTools,
  errorCount = 0,
  errorsActive,
  onShowErrors,
  selectActive,
  onToggleSelect,
  onShowScoring,
  onPreview,
  scoringActive,
  readOnly,
}: ExperienceTitleBarProps) {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLDivElement>(null);
  const lastSaved = useRef(title);
  const isFocusedRef = useRef(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isEmpty, setIsEmpty] = useState(!title);

  useEffect(() => {
    if (titleRef.current) titleRef.current.textContent = title;
  }, []);

  useEffect(() => {
    lastSaved.current = title;
    setIsEmpty(!title);
    if (titleRef.current && !isFocusedRef.current) {
      titleRef.current.textContent = title;
    }
  }, [title]);

  function handleFocus() {
    isFocusedRef.current = true;
    setIsFocused(true);
  }

  function handleBlur() {
    isFocusedRef.current = false;
    setIsFocused(false);
    const text = titleRef.current?.textContent?.trim() ?? '';
    setIsEmpty(!text);
    if (!text) {
      if (titleRef.current) titleRef.current.textContent = lastSaved.current;
      setIsEmpty(!lastSaved.current);
      return;
    }
    if (text !== lastSaved.current) {
      lastSaved.current = text;
      onTitleSave(text);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
    if (e.key === 'Escape') {
      if (titleRef.current) titleRef.current.textContent = lastSaved.current;
      e.currentTarget.blur();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain').replace(/\n/g, ' ');
    document.execCommand('insertText', false, text);
  }

  function handleInput() {
    setIsEmpty(!(titleRef.current?.textContent?.trim()));
  }

  return (
    <div className='flex min-h-14 shrink-0 items-center gap-3 border-b border-ws-border bg-ws-surface px-3 py-2'>

      <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded bg-ws-primary'>
        <Waypoints aria-hidden='true' className='h-6 w-6 text-white' />
      </div>

      <div className={`relative min-w-0 ${isFocused ? 'flex-1 max-w-xl' : ''}`}>
        {isEmpty && !isFocused && (
          <span className='absolute inset-0 text-lg font-semibold text-ws-muted px-2 py-0.5 pointer-events-none select-none'>
            {t('experience.titlePlaceholder')}
          </span>
        )}
        <div
          ref={titleRef}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          role='textbox'
          tabIndex={0}
          aria-label={t('experience.titlePlaceholder')}
          aria-placeholder={t('experience.titlePlaceholder')}
          aria-multiline='true'
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onInput={handleInput}
          className={`rounded-md bg-black/[0.04] dark:bg-white/[0.06] text-lg font-semibold text-ws-text
                      border-b-2 border-transparent transition-colors
                      hover:bg-black/[0.07] dark:hover:bg-white/[0.10]
                      focus:bg-black/[0.04] dark:focus:bg-white/[0.06] focus:border-ws-primary focus:outline-none
                      px-2 py-0.5 break-words
                      ${isFocused ? 'whitespace-normal' : 'whitespace-nowrap overflow-hidden text-ellipsis'}`}
        />
      </div>

      {/* Centre tools: Errors · Scoring · Preview (stubs). Two flex spacers centre the group between the
          title and the command cluster; with tools off (legacy canvas) a single spacer pushes commands right. */}
      {showTools ? (
        <>
          <div className='flex-1' />
          <div className='flex shrink-0 items-center gap-1'>
            <button
              type='button'
              onClick={onShowErrors}
              aria-pressed={errorsActive}
              className={`flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors ${
                errorsActive ? 'bg-ws-error-bg text-ws-error-fg' : 'text-ws-muted hover:bg-ws-hover hover:text-ws-text'}`}
              aria-label={errorCount > 0
                ? t('experience.tools.errorsWithCount', { count: errorCount })
                : t('experience.tools.errors')}
              title={t('experience.tools.errors')}
            >
              <TriangleAlert aria-hidden='true' className='h-4 w-4' />
              <span className='hidden lg:inline'>{t('experience.tools.errors')}</span>
              {/* THE COUNT IS IN THE NAME TOO, not only in the badge: a listener gets "Errors, 7"
                  from the label rather than a bare "Errors" beside a number they cannot see. */}
              {errorCount > 0 && (
                <span aria-hidden='true' className='min-w-[20px] rounded-full bg-ws-error-fg px-1.5 text-xs font-bold leading-5 text-white'>
                  {errorCount}
                </span>
              )}
            </button>
            <button
              type='button'
              onClick={onShowScoring}
              aria-pressed={scoringActive}
              className={`flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors ${scoringActive ? 'bg-ws-primary/10 text-ws-primary' : 'text-ws-muted hover:bg-ws-hover hover:text-ws-text'}`}
              aria-label={t('experience.tools.scoring')}
              title={t('experience.tools.scoring')}
            >
              <Trophy aria-hidden='true' className='h-4 w-4' />
              <span className='hidden lg:inline'>{t('experience.tools.scoring')}</span>
            </button>
            <button
              type='button'
              onClick={onToggleSelect}
              aria-pressed={selectActive}
              className={`flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors ${selectActive ? 'bg-ws-primary/10 text-ws-primary' : 'text-ws-muted hover:bg-ws-hover hover:text-ws-text'}`}
              aria-label={t('experience.select.title')}
              title={t('experience.select.title')}
            >
              <CheckSquare aria-hidden='true' className='h-4 w-4' />
              <span className='hidden lg:inline'>{t('experience.select.title')}</span>
            </button>
            <Button variant='primary' onClick={onPreview} aria-label={t('experience.tools.preview')} title={t('experience.tools.preview')}
              icon={<Play aria-hidden='true' fill='currentColor' />}>
              {t('experience.tools.preview')}
            </Button>
          </div>
          <div className='flex-1' />
        </>
      ) : (
        <div className='flex-1' />
      )}

      {/* Commands: undo · redo · save */}
      <div className='flex shrink-0 items-center gap-0.5'>
        <button
          onClick={onUndo}
          disabled={!canUndo || readOnly}
          className='flex h-8 w-8 items-center justify-center rounded-md hover:bg-ws-hover disabled:opacity-30 disabled:pointer-events-none'
          aria-label={undoLabel ? t('nav.undoLabel', { label: undoLabel }) : t('nav.undo')}
          title={undoLabel ? t('nav.undoLabel', { label: undoLabel }) : t('nav.undo')}
        >
          <Undo2 className='h-4 w-4 text-ws-text' />
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo || readOnly}
          className='flex h-8 w-8 items-center justify-center rounded-md hover:bg-ws-hover disabled:opacity-30 disabled:pointer-events-none'
          aria-label={redoLabel ? t('nav.redoLabel', { label: redoLabel }) : t('nav.redo')}
          title={redoLabel ? t('nav.redoLabel', { label: redoLabel }) : t('nav.redo')}
        >
          <Redo2 className='h-4 w-4 text-ws-text' />
        </button>

        {/* Same button drives manual save + autosave status; fixed min-width keeps undo/redo from
         * shifting as the label changes (see TaskTitlebar's own note). */}
        <button
          onClick={onSave}
          disabled={saving || !isDirty || readOnly}
          aria-label={saving ? t('experience.titleBar.saving') : saveFailed ? t('experience.titleBar.saveFailed') : !isDirty ? t('experience.titleBar.saved') : t('experience.titleBar.save')}
          title={saveFailed ? t('experience.titleBar.saveFailed') : undefined}
          className={`ml-3 flex h-8 min-w-[110px] items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors disabled:pointer-events-none
                      ${saveFailed && !saving
                        ? 'bg-ws-error-fg/10 text-ws-error-fg hover:opacity-90'
                        : 'bg-ws-primary text-white hover:opacity-90 disabled:opacity-40'}`}
        >
          {!isDirty && !saving && <Check aria-hidden='true' className='h-4 w-4' />}
          <span aria-hidden='true'>
            {saving ? t('experience.titleBar.saving') : saveFailed ? t('experience.titleBar.saveFailed') : !isDirty ? t('experience.titleBar.saved') : t('experience.titleBar.save')}
          </span>
        </button>
        <div aria-live='polite' aria-atomic='true' className='sr-only'>
          {saving ? t('experience.titleBar.saving') : saveFailed ? t('experience.titleBar.saveFailed') : !isDirty ? t('experience.titleBar.saved') : ''}
        </div>
      </div>
    </div>
  );
}
